import { useMemo, useState } from "react";
import type { ApprovalStatus, Distributor } from "@/types";
import { formatCOP, formatDate } from "@/utils/format";

interface PendingApprovalsProps {
  distributors: Distributor[];
  onSetStatus: (id: string, status: ApprovalStatus) => void;
  onOpenFicha: (d: Distributor) => void;
  busyId?: string;
}

interface StatusTab {
  key: ApprovalStatus;
  label: string;
  icon: string;
}

const TABS: StatusTab[] = [
  { key: "pending", label: "Pendientes", icon: "ri-time-line" },
  { key: "approved", label: "Aprobados", icon: "ri-checkbox-circle-line" },
  { key: "rejected", label: "Rechazados", icon: "ri-close-circle-line" },
  { key: "suspended", label: "Suspendidos", icon: "ri-pause-circle-line" },
];

const STATUS_BADGE: Record<ApprovalStatus, { label: string; icon: string; cls: string }> = {
  pending: { label: "Pendiente", icon: "ri-time-line", cls: "bg-accent-500/15 text-accent-200" },
  approved: {
    label: "Aprobado",
    icon: "ri-checkbox-circle-line",
    cls: "bg-accent-500/20 text-accent-200",
  },
  rejected: {
    label: "Rechazada",
    icon: "ri-close-line",
    cls: "bg-primary-500/20 text-primary-100",
  },
  suspended: {
    label: "Suspendido",
    icon: "ri-pause-circle-line",
    cls: "bg-secondary-500/25 text-secondary-100",
  },
};

// Módulo de registro de asesores: revisa solicitudes y gestiona el estado de cada cuenta.
const PendingApprovals = ({
  distributors,
  onSetStatus,
  onOpenFicha,
  busyId,
}: PendingApprovalsProps) => {
  const [tab, setTab] = useState<ApprovalStatus>("pending");
  const [query, setQuery] = useState("");

  const counts = useMemo<Record<ApprovalStatus, number>>(
    () => ({
      pending: distributors.filter((d) => d.approvalStatus === "pending").length,
      approved: distributors.filter((d) => d.approvalStatus === "approved").length,
      rejected: distributors.filter((d) => d.approvalStatus === "rejected").length,
      suspended: distributors.filter((d) => d.approvalStatus === "suspended").length,
    }),
    [distributors]
  );

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return distributors.filter((d) => {
      if (d.approvalStatus !== tab) return false;
      if (!q) return true;
      return (
        d.names.toLowerCase().includes(q) ||
        d.surnames.toLowerCase().includes(q) ||
        d.cedula.toLowerCase().includes(q) ||
        d.city.toLowerCase().includes(q) ||
        d.email.toLowerCase().includes(q) ||
        d.phone.toLowerCase().includes(q)
      );
    });
  }, [distributors, tab, query]);

  return (
    <section className="glass-panel rounded-lg overflow-hidden">
      <header className="flex flex-col gap-3 px-4 md:px-5 py-4 border-b border-accent-500/15">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg bg-accent-500/20 text-accent-200 flex items-center justify-center shrink-0">
              <i className="ri-user-received-2-line text-lg" />
            </span>
            <div>
              <h3 className="editable-title text-base font-bold">Registro de asesores</h3>
              <p className="text-[11px] text-foreground-600">
                Filtra por estado y busca en tu red. Aprueba, rechaza, suspende o reactiva cuentas.
              </p>
            </div>
          </div>
          {counts.pending > 0 && (
            <span className="self-start inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-primary-500/20 text-primary-100 whitespace-nowrap">
              <i className="ri-notification-3-line" /> {counts.pending} por revisar
            </span>
          )}
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex flex-wrap items-center gap-1 bg-background-200/60 rounded-full p-1 w-full sm:w-auto">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                    active
                      ? "bg-primary-500 text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <i className={t.icon} />
                  {t.label}
                  <span
                    className={`text-[10px] px-1.5 rounded-full ${
                      active ? "bg-background-50/25" : "bg-background-300/40"
                    }`}
                  >
                    {counts[t.key]}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:flex-1 lg:max-w-xs">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
              <i className="ri-search-line" />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre, cédula, ciudad..."
              className="w-full bg-background-100/70 border border-background-300/50 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/25"
              aria-label="Buscar en el registro de asesores"
            />
          </div>
        </div>
      </header>

      {list.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
            <i className="ri-checkbox-circle-line text-2xl" />
          </span>
          <p className="text-sm text-foreground-700 font-semibold">
            {query.trim() ? "Sin resultados para tu búsqueda" : "No hay cuentas en este estado"}
          </p>
          <p className="text-xs text-foreground-500 mt-1">
            {query.trim()
              ? "Prueba con otro nombre, cédula o ciudad."
              : "Cuando alguien se registre desde la pantalla de acceso, aparecerá aquí."}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-background-300/20">
          {list.map((d) => {
            const badge = STATUS_BADGE[d.approvalStatus];
            const busy = busyId === d.id;
            return (
              <li
                key={d.id}
                className="px-4 md:px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-3 hover:bg-background-200/25 transition-colors"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span className="w-10 h-10 rounded-full bg-primary-500/25 text-accent-200 flex items-center justify-center shrink-0 font-semibold">
                    {d.names.charAt(0).toUpperCase() || "?"}
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-foreground-950 truncate">
                        {d.names} {d.surnames}
                      </p>
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}
                      >
                        <i className={badge.icon} />
                        {badge.label}
                      </span>
                      {d.approvalStatus === "pending" && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary-500/20 text-primary-100"
                          title="Define su margen comercial y cupo de consignación en la ficha máster"
                        >
                          <i className="ri-price-tag-3-line" />
                          Condiciones por asignar
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-foreground-600 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-id-card-line" /> {d.cedula || "Sin cédula"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-phone-line" /> {d.phone || "—"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-map-pin-line" /> {d.city || "—"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-mail-line" /> {d.email || "—"}
                      </span>
                    </p>
                    <p className="text-[10px] text-foreground-500 mt-1">
                      Referencia: {d.reference.name || "—"}
                      {d.reference.relationship ? ` (${d.reference.relationship})` : ""} ·{" "}
                      {d.reference.phone || "—"} · Solicitud el {formatDate(d.createdAt)}
                    </p>
                    {d.consignmentLimit > 0 && (
                      <p className="text-[10px] text-foreground-500 mt-0.5">
                        Solicita cupo de consignación: {formatCOP(d.consignmentLimit)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
                  {d.approvalStatus === "pending" && (
                    <>
                      <button
                        onClick={() => onSetStatus(d.id, "approved")}
                        disabled={busy}
                        className="px-3.5 py-2 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                      >
                        {busy ? (
                          <i className="ri-loader-4-line animate-spin" />
                        ) : (
                          <i className="ri-check-line" />
                        )}
                        Aprobar
                      </button>
                      <button
                        onClick={() => onSetStatus(d.id, "rejected")}
                        disabled={busy}
                        className="px-3.5 py-2 rounded-md border border-primary-400/40 text-primary-100 hover:bg-primary-500/10 disabled:opacity-60 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                      >
                        <i className="ri-close-line" /> Rechazar
                      </button>
                    </>
                  )}

                  {d.approvalStatus === "rejected" && (
                    <button
                      onClick={() => onSetStatus(d.id, "approved")}
                      disabled={busy}
                      className="px-3.5 py-2 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                    >
                      {busy ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-refresh-line" />
                      )}
                      Reconsiderar
                    </button>
                  )}

                  {d.approvalStatus === "approved" && (
                    <button
                      onClick={() => onSetStatus(d.id, "suspended")}
                      disabled={busy}
                      className="px-3.5 py-2 rounded-md border border-secondary-400/40 text-secondary-100 hover:bg-secondary-500/10 disabled:opacity-60 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                    >
                      {busy ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-pause-circle-line" />
                      )}
                      Suspender
                    </button>
                  )}

                  {d.approvalStatus === "suspended" && (
                    <button
                      onClick={() => onSetStatus(d.id, "approved")}
                      disabled={busy}
                      className="px-3.5 py-2 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                    >
                      {busy ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-play-circle-line" />
                      )}
                      Reactivar
                    </button>
                  )}

                  <button
                    onClick={() => onOpenFicha(d)}
                    className="px-3.5 py-2 rounded-md border border-accent-500/30 text-foreground-700 hover:text-foreground-950 hover:border-accent-500/50 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                    title="Abrir ficha máster"
                  >
                    <i className="ri-id-card-line" /> Ficha
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default PendingApprovals;